import {
  Autocomplete,
  AutocompleteInputChangeReason,
  Avatar,
  Paper,
  TextField,
} from '@mui/material';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { BlueArrowDown } from 'Images';
import { FC, useEffect, useRef, useState } from 'react';
import { useController, UseControllerProps } from 'react-hook-form';
import { generateError } from 'Regex';
import { styleTextField } from 'Theme';

export interface Option {
  id: number;
  value: string;
}

interface IControllerAutoComplete extends UseControllerProps {
  label?: string;
  options: Option[];
  disabled?: boolean;
}

export const MyPaper: FC<{}> = (props) => {
  return (
    <Paper
      sx={{
        position: 'fixed',
        borderRadius: `0 0 6px 6px`,
        width: '100%',
        fontSize: '14px',
        padding: '0',
        zIndex: 10, // Ensure it appears above other elements if needed
      }}
    >
      {props.children}
    </Paper>
  );
};

export const ControllerAutoComplete = (props: IControllerAutoComplete) => {
  const { label, options, disabled, ...controller } = props;
  const {
    fieldState: { error },
    field: { onChange, onBlur, ref, ...field },
  } = useController(controller);

  const [input, setInput] = useState<string>('');

  const inputHandler = (
    e: React.SyntheticEvent<Element, Event>,
    v: string,
    r: AutocompleteInputChangeReason
  ) => {
    switch (r) {
      case 'clear':
        break;
      case 'reset':
        break;
      case 'input':
        setInput(v);
        break;
      default:
        break;
    }
  };

  const reset = useRef<string>('');

  const findOptionLabel = (id: number) => {
    const option = options.find((option) => option.id == id);
    if (option) {
      setInput(option.value);
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (field.value && reset.current !== 'DONE' && options.length) {
      const option = findOptionLabel(field.value);
      if (option) {
        reset.current = 'DONE';
      }
    }
  }, [field.value, options]);

  return (
    <Autocomplete
      {...field}
      disablePortal
      id={label}
      onChange={(e, v, r) => {
        if (v) {
          setInput(v?.value);
          onChange(v ? v.id : v);
        }
      }}
      PaperComponent={MyPaper}
      noOptionsText={Dictionary[DICTIONARY.GENERAL].noOptions}
      options={options}
      inputValue={disabled ? '' : input}
      onBlur={(e) => {
        if (field.value) {
          findOptionLabel(field.value);
        } else {
          setInput('');
        }
        onBlur();
      }}
      onInputChange={inputHandler}
      disableClearable
      getOptionLabel={(option: Option) => option.value || ''}
      renderInput={(params) => (
        <TextField
          role={Boolean(error) ? 'alert' : undefined}
          inputRef={ref}
          label={label}
          error={Boolean(error)}
          helperText={error?.message && generateError(label!, error.message)}
          {...params}
          sx={{
            ...styleTextField,
            '& input': {
              p: '0 0 0 14px !important',
              color: '#39556B',
            },
          }}
        />
      )}
      disabled={disabled}
      popupIcon={
        <Avatar
          alt=''
          src={BlueArrowDown}
          sx={{
            width: '32px',
            height: '32px',
            '& > img': {
              width: '27px',
              height: '27px',
              mb: 'auto',
            },
          }}
        />
      }
    />
  );
};
